import { ThunderboltOutlined } from '@ant-design/icons';
import type { ProductInput } from '@shop-monorepo/types';
import { isValidOldPrice } from '@shop-monorepo/types';
import { Button, Form, Input, InputNumber, Select, Switch, TreeSelect } from 'antd';
import React from 'react';
import styled from 'styled-components';

import { useBrands } from '@/hooks/useBrandQueries';
import { useCategories } from '@/hooks/useCategoryQueries';
import { useGenerateProductDescription } from '@/hooks/useProductQueries';

import { FilePicker } from '../../components/FilePicker';
import { FormCard } from '../../components/PageLayout';
import { RichTextEditor } from '../../components/RichTextEditor/RichTextEditor';
import { transformToTreeData } from '../../utils/categoryUtils';
import { convertCoinsToPrice, convertPriceToCoins, OLD_PRICE_ERROR } from '../../utils/currency';

import { ProductDuplicateWarning } from './ProductDuplicateWarning';

interface ProductFormProps {
  initialValues?: Partial<ProductInput>;
  onSubmit: (values: ProductInput) => Promise<void>;
  isSubmitting: boolean;
  excludeProductId?: number;
}

const TwoColGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  > * {
    min-width: 0;
  }
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  padding-bottom: 14px;
  margin-bottom: 20px;
  border-bottom: 1px solid #edf2f7;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const StatusLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;

const StatusHint = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 6px 0 0;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface DescriptionFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  productName?: string;
  isGenerating?: boolean;
  onGenerate?: (name: string) => void;
}

const DescriptionField: React.FC<DescriptionFieldProps> = ({
  value,
  onChange,
  productName,
  isGenerating,
  onGenerate,
}) => (
  <DescriptionWrapper>
    <DescriptionToolbar>
      <Button
        size="small"
        icon={<ThunderboltOutlined />}
        disabled={!productName}
        loading={isGenerating}
        onClick={() => productName && onGenerate?.(productName)}
      >
        Згенерувати
      </Button>
    </DescriptionToolbar>
    <RichTextEditor value={value} onChange={onChange} />
  </DescriptionWrapper>
);

export const ProductForm: React.FC<ProductFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting,
  excludeProductId,
}) => {
  const [form] = Form.useForm();
  const productName = Form.useWatch('name', form);
  const thumbnailUrl = Form.useWatch('thumbnail', form);

  const { data: brands = [] } = useBrands();
  const { data: categories = [] } = useCategories();
  const { mutate: generateDescription, isPending: isGenerating } = useGenerateProductDescription();

  const handleFinish = async (values: ProductInput) => {
    values.oldPrice = values.oldPrice === undefined ? null : values.oldPrice;
    values.brandId = values.brandId === undefined ? null : values.brandId;
    values.isActive = values.isActive ?? false;
    values.isNew = values.isNew ?? false;

    const productToSubmit = {
      ...values,
      price: convertPriceToCoins(values.price as number),
      oldPrice: convertPriceToCoins(values.oldPrice as number | null),
    };

    try {
      await onSubmit(productToSubmit);
      form.resetFields();
    } catch {
      // Save failed — keep the entered values so the admin can fix and retry.
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        isActive: true,
        ...initialValues,
        price: convertCoinsToPrice(initialValues?.price),
        oldPrice: convertCoinsToPrice(initialValues?.oldPrice),
      }}
    >
      <TwoColGrid>
        <div>
          <FormCard>
            <CardTitle>Загальна інформація</CardTitle>
            <Form.Item
              name="name"
              label="Назва"
              rules={[{ required: true, message: 'Будь ласка, введіть назву продукту' }]}
            >
              <Input />
            </Form.Item>
            <ProductDuplicateWarning name={productName} excludeProductId={excludeProductId} />
            <Form.Item
              name="description"
              label="Опис"
              rules={[{ required: true, message: 'Будь ласка, введіть опис продукту' }]}
            >
              <DescriptionField
                productName={productName}
                isGenerating={isGenerating}
                onGenerate={(name) =>
                  generateDescription(name, {
                    onSuccess: (data) => form.setFieldValue('description', data.description),
                  })
                }
              />
            </Form.Item>
          </FormCard>

          <Form.Item style={{ marginTop: 8 }}>
            <Button type="primary" htmlType="submit" loading={isSubmitting} size="large">
              Зберегти товар
            </Button>
          </Form.Item>
        </div>

        <div>
          <FormCard>
            <StatusRow>
              <div>
                <StatusLabel>Статус товару</StatusLabel>
                <StatusHint>Товар буде видимий для покупців</StatusHint>
              </div>
              <Form.Item name="isActive" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </StatusRow>
            <StatusRow style={{ marginTop: 16 }}>
              <div>
                <StatusLabel>Новинка</StatusLabel>
                <StatusHint>Позначити як новий товар</StatusHint>
              </div>
              <Form.Item name="isNew" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </StatusRow>
          </FormCard>

          <FormCard>
            <CardTitle>Класифікація</CardTitle>
            <Form.Item name="categoryIds" label="Категорії">
              <TreeSelect
                treeData={transformToTreeData(categories)}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                placeholder="Виберіть категорії"
                allowClear
                treeDefaultExpandAll
                showSearch={{ treeNodeFilterProp: 'title' }}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
            </Form.Item>
            <Form.Item name="brandId" label="Бренд">
              <Select
                placeholder="Виберіть бренд"
                allowClear
                showSearch={{ optionFilterProp: 'label' }}
                options={brands.map((brand) => ({
                  label: brand.name,
                  value: brand.id,
                }))}
              />
            </Form.Item>
          </FormCard>

          <FormCard>
            <CardTitle>Ціна та зображення</CardTitle>
            <Form.Item
              name="price"
              label="Ціна"
              rules={[{ required: true, message: 'Будь ласка, введіть ціну продукту' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="oldPrice"
              label="Стара ціна"
              dependencies={['price']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (isValidOldPrice(getFieldValue('price'), value)) return Promise.resolve();
                    return Promise.reject(new Error(OLD_PRICE_ERROR));
                  },
                }),
              ]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="image"
              label="Зображення"
              rules={[{ required: true, message: 'Будь ласка, завантажте зображення' }]}
            >
              <FilePicker
                thumbnailUrl={thumbnailUrl}
                onThumbnailChange={(url) => form.setFieldValue('thumbnail', url)}
              />
            </Form.Item>
            <Form.Item name="thumbnail" hidden>
              <Input />
            </Form.Item>
          </FormCard>
        </div>
      </TwoColGrid>
    </Form>
  );
};
